import { removeNativePushToken } from "../../helpers/nativePush";
import {
	LOGIN_USER,
	REGISTER_USER,
	LOGOUT_USER,
	UPDATE_USER_INFO,
	SEND_OTP,
	VERIFY_OTP,
	LOGIN_WITH_OTP,
	RUNNING_ORDER,
	GET_WALLET_TRANSACTIONS,
	SEND_PASSWORD_RESET_EMAIL,
	VERIFY_PASSWORD_RESET_OTP,
	CHANGE_USER_PASSWORD,
	SAVE_VAT_NUMBER,
} from "./actionTypes";
import {
	LOGIN_USER_URL,
	REGISTER_USER_URL,
	UPDATE_USER_INFO_URL,
	SEND_OTP_URL,
	VERIFY_OTP_URL,
	LOGIN_WITH_OTP_URL,
	SOCIAL_LOGIN_URL,
	GENERATE_LOGIN_OTP_URL,
	CHECK_USER_RUNNING_ORDER_URL,
	GET_WALLET_TRANSACTIONS_URL,
	SEND_PASSWORD_RESET_EMAIL_URL,
	VERIFY_PASSWORD_RESET_OTP_URL,
	CHANGE_USER_PASSWORD_URL,
	CHANGE_USER_AVATAR_URL,
	CHECK_BAN_URL,
	SAVE_VAT_NUMBER_URL,
	ADD_TO_FAVORITE_RESTAURANT_URL,
	DELETE_USER_ACCOUNT_URL,
} from "../../configs";
import { WEBSITE_URL } from "../../configs/website";
import { GET_ADDRESSES } from "../addresses/actionTypes";
import { updateCart } from "../total/actions";
import {
	loadCart,
	loadPhase2Cart,
	updateCartProduct,
	updatePhase2CartProduct,
	hydrateCartFromBackend,
	clearAllCartsOnLogout,
} from "../cart/actions";
import { AppMode } from "../../constants/app-mode.const";
import Axios from "axios";

// Helper function to merge guest favorites with backend after login
const mergeGuestFavorites = async (authToken) => {
	try {
		const guestFavorites = localStorage.getItem("guest_favorites");
		if (!guestFavorites) return;

		const favorites = JSON.parse(guestFavorites);
		if (!favorites || favorites.length === 0) return;

		console.log("Merging guest favorites with backend:", favorites);

		// Send each favorite to backend (toggle-favorite API adds if not exists)
		for (const fav of favorites) {
			const parts = fav.split("_");
			if (parts.length < 2) continue;

			const type = parts[0]; // 'item' or 'restaurant'
			const id = parseInt(parts[1]);

			if (isNaN(id)) continue;

			await Axios.post(
				ADD_TO_FAVORITE_RESTAURANT_URL,
				{
					token: authToken,
					id: id,
					type: type,
				},
				{
					headers: { Authorization: `Bearer ${authToken}` },
				}
			);
		}

		// Clear guest favorites after successful merge
		localStorage.removeItem("guest_favorites");
		console.log("Guest favorites merged successfully");
	} catch (error) {
		console.error("Error merging guest favorites:", error);
	}
};

export const loginUser = (name, email, password, accessToken, phone, provider, address) => (dispatch, getState) => {
	return Axios.post(LOGIN_USER_URL, {
		name: name,
		email: email,
		password: password,
		accessToken: accessToken,
		phone: phone,
		provider: provider,
		address: address,
	})
		.then((response) => {
			const user = response.data;

			// Sync current_cart_type from backend if available
			if (user && user.data && user.data.current_cart_type) {
				// current_cart_type is already in user.data, will be stored in state
				console.log('User current_cart_type synced from backend:', user.data.current_cart_type);
			}

			// Dispatch login first to update user state
			dispatch({ type: LOGIN_USER, payload: user });

			// Get app mode from settings to determine which cart to restore
			const state = getState();
			const appMode = state.settings && state.settings.appMode ? state.settings.appMode : AppMode.SPRINT;

			// Restore Sprint cart items from localStorage (guest browsing)
			const savedSprintCart = localStorage.getItem('savedCartItems_sprint');
			if (savedSprintCart) {
				try {
					const parsedSprintCart = JSON.parse(savedSprintCart);
					if (parsedSprintCart && parsedSprintCart.length > 0) {
						// Load cart into Redux state
						dispatch(loadCart(parsedSprintCart));
						// Update cart totals
						dispatch(updateCart(parsedSprintCart));
						// Sync to backend (will only sync if user is logged in)
						dispatch(updateCartProduct());
					}
				} catch (e) {
					console.error("Error parsing Sprint cart from localStorage:", e);
				}
			}

			// Restore Relay cart items from localStorage (guest browsing)
			const savedRelayCart = localStorage.getItem('savedCartItems_relay');
			if (savedRelayCart) {
				try {
					const parsedRelayCart = JSON.parse(savedRelayCart);
					if (parsedRelayCart && parsedRelayCart.length > 0) {
						// Load cart into Redux state
						dispatch(loadPhase2Cart(parsedRelayCart));
						// Sync to backend (will only sync if user is logged in)
						dispatch(updatePhase2CartProduct());
					}
				} catch (e) {
					console.error("Error parsing Relay cart from localStorage:", e);
				}
			}

			// Also check for old localStorage key for backward compatibility
			const savedCartItems = localStorage.getItem('savedCartItems');
			if (savedCartItems && !savedSprintCart) {
				try {
					const parsedCartItems = JSON.parse(savedCartItems);
					if (parsedCartItems && parsedCartItems.length > 0) {
						// Migrate old cart to new format
						dispatch(loadCart(parsedCartItems));
						dispatch(updateCart(parsedCartItems));
						dispatch(updateCartProduct());
						// Save to new key
						localStorage.setItem('savedCartItems_sprint', savedCartItems);
						localStorage.removeItem('savedCartItems');
					}
				} catch (e) {
					console.error("Error parsing old cart from localStorage:", e);
				}
			}

			// Merge guest favorites with backend
			if (user && user.data && user.data.auth_token) {
				mergeGuestFavorites(user.data.auth_token);
				// If guest localStorage was empty, restore from users.*_cart_items
				dispatch(hydrateCartFromBackend());
			}

			return user;
		})
		.catch(function (error) {
			console.log(error);
			throw error;
		});
};

export const registerUser = (name, email, phone, password, address) => (dispatch) => {

	Axios.post(REGISTER_USER_URL, {
		name: name,
		email: email,
		phone: phone,
		password: password,
		address: address,
	})
		.then((response) => {
			const user = response.data;
			return dispatch({ type: REGISTER_USER, payload: user });
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const logoutUser = (options = {}) => (dispatch, getState) => {
	// Stop native pushes to this device for the user who is logging out.
	const currentUser = getState().user && getState().user.user;
	removeNativePushToken(currentUser && currentUser.data && currentUser.data.auth_token);

	const user = [];
	const walletEmpty = [];
	const emptyAddress = [];

	//remove geoLocation and userSetAddress
	localStorage.removeItem("userSetAddress");
	localStorage.removeItem("geoLocation");
	localStorage.removeItem("locationManuallyChanged"); // Clear manual location flag on logout (for backward compatibility)
	sessionStorage.removeItem("locationManuallyChanged"); // Clear sessionStorage manual location flag on logout
	sessionStorage.removeItem("locationManuallyChangedTime"); // Clear timestamp on logout
	localStorage.removeItem("lastSavedNotificationToken");
	localStorage.removeItem("orderComment");

	return clearAllCartsOnLogout({
		skipServerSync: Boolean(options.skipServerCartClear),
	})(dispatch, getState).then(() => [
		dispatch({
			type: LOGOUT_USER,
			payload: user,
		}),
		dispatch({
			type: RUNNING_ORDER,
			payload: false,
		}),
		dispatch({
			type: GET_WALLET_TRANSACTIONS,
			payload: walletEmpty,
		}),
		dispatch({
			type: GET_ADDRESSES,
			payload: emptyAddress,
		}),
	]);
};

export const updateUserInfo = (user_id, token, unique_order_id) => (dispatch) => {
	return Axios.post(UPDATE_USER_INFO_URL, {
		token: token,
		user_id: user_id,
		unique_order_id: unique_order_id,
	}, {
		headers: {
			"Content-Type": "application/json",
			...(token && { Authorization: `Bearer ${token}` }),
		},
	})
		.then((response) => {
			const user = response.data;
			return dispatch({ type: UPDATE_USER_INFO, payload: user });
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const saveUserProfile = (token, user_id, name, email) => (dispatch) => {
	return Axios.post(UPDATE_USER_INFO_URL, {
		token: token,
		user_id: user_id,
		name: name,
		email: email,
	}, {
		headers: {
			"Content-Type": "application/json",
			...(token && { Authorization: `Bearer ${token}` }),
		},
	})
		.then((response) => {
			const user = response.data;
			if (user.success) {
				dispatch({ type: UPDATE_USER_INFO, payload: user });
			}
			return user;
		})
		.catch(function (error) {
			const message =
				(error &&
					error.response &&
					error.response.data &&
					error.response.data.message) ||
				(error && error.message) ||
				"Unable to update profile.";
			return { success: false, message };
		});
};

export const checkUserRunningOrder = (user_id, token) => (dispatch) => {
	return Axios.post(
		CHECK_USER_RUNNING_ORDER_URL,
		{
			token: token,
			user_id: user_id,
		},
		{
			headers: {
				...(token && { Authorization: `Bearer ${token}` }),
			},
		}
	)
		.then((response) => {
			const running_order = response.data;
			return dispatch({ type: RUNNING_ORDER, payload: running_order });
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const sendOtp = (email, phone, accessToken, provider) => (dispatch) => {
	return Axios.post(SEND_OTP_URL, {
		email: email,
		phone: phone,
		accessToken: accessToken,
		provider: provider,
	})
		.then((response) => {
			const user = response.data;
			return dispatch({ type: SEND_OTP, payload: user });
		})
		.catch(function (error) {
			console.error("❌ [API DEBUG] sendOtp error occurred");
			console.error("❌ [API DEBUG] Error:", error);
			console.error("❌ [API DEBUG] Error response:", error.response);
			console.error("❌ [API DEBUG] Error message:", error.message);
			if (error.response) {
				console.error("❌ [API DEBUG] Error status:", error.response.status);
				console.error("❌ [API DEBUG] Error data:", error.response.data);
			}
		});
};

export const generateOtpForLogin = (phone, email = null) => (dispatch) => {
	return Axios.post(GENERATE_LOGIN_OTP_URL, {
		phone: phone,
		email: email,
	})
		.then((response) => {
			const user = response.data;
			return dispatch({ type: SEND_OTP, payload: user });
		})
		.catch(function (error) {
			console.error("❌ [API DEBUG] generateOtpForLogin error occurred");
			console.error("❌ [API DEBUG] Error:", error);
			console.error("❌ [API DEBUG] Error response:", error.response);
			console.error("❌ [API DEBUG] Error message:", error.message);
			if (error.response) {
				console.error("❌ [API DEBUG] Error status:", error.response.status);
				console.error("❌ [API DEBUG] Error data:", error.response.data);
			}
		});
};

export const verifyOtp = (phone, otp) => (dispatch) => {
	return Axios.post(VERIFY_OTP_URL, {
		phone: phone,
		otp: otp,
	})
		.then((response) => {
			const user = response.data;
			return dispatch({ type: VERIFY_OTP, payload: user });
		})
		.catch(function (error) {
			console.error("❌ [API DEBUG] verifyOtp error occurred");
			console.error("❌ [API DEBUG] Error:", error);
			console.error("❌ [API DEBUG] Error response:", error.response);
			console.error("❌ [API DEBUG] Error message:", error.message);
			if (error.response) {
				console.error("❌ [API DEBUG] Error status:", error.response.status);
				console.error("❌ [API DEBUG] Error data:", error.response.data);
			}
		});
};

export const loginWithOtp = (phone, otp, address) => {
	const payload = {
		phone: phone,
		otp: otp,
	};
	if (address) {
		payload.address = address;
	}
	return loginViaEndpoint(LOGIN_WITH_OTP_URL, payload);
};

/** Google / Apple sign-in: the ID token is verified server-side; no OTP/phone needed. */
export const socialLogin = (provider, idToken, address, name) => {
	const payload = { provider: provider, id_token: idToken };
	if (address) {
		payload.address = address;
	}
	if (name) {
		payload.name = name;
	}
	return loginViaEndpoint(SOCIAL_LOGIN_URL, payload);
};

// Shared post-login handling (token storage, cart/favourites restore) for OTP and social login.
const loginViaEndpoint = (url, payload) => (dispatch, getState) => {
	return Axios.post(url, payload)
		.then((response) => {
			const user = response.data;

			// Save auth token to localStorage if available
			if (user && user.data && user.data.auth_token) {
				localStorage.setItem('authToken', user.data.auth_token);
			}

			// Dispatch login first to update user state
			dispatch({ type: LOGIN_WITH_OTP, payload: user });

			// Get app mode from settings to determine which cart to restore
			const state = getState();
			const appMode = state.settings && state.settings.appMode ? state.settings.appMode : AppMode.SPRINT;

			// Restore Sprint cart items from localStorage (guest browsing)
			const savedSprintCart = localStorage.getItem('savedCartItems_sprint');
			if (savedSprintCart) {
				try {
					const parsedSprintCart = JSON.parse(savedSprintCart);
					if (parsedSprintCart && parsedSprintCart.length > 0) {
						// Load cart into Redux state
						dispatch(loadCart(parsedSprintCart));
						// Update cart totals
						dispatch(updateCart(parsedSprintCart));
						// Sync to backend (will only sync if user is logged in)
						dispatch(updateCartProduct());
					}
				} catch (e) {
					console.error("Error parsing Sprint cart from localStorage:", e);
				}
			}

			// Restore Relay cart items from localStorage (guest browsing)
			const savedRelayCart = localStorage.getItem('savedCartItems_relay');
			if (savedRelayCart) {
				try {
					const parsedRelayCart = JSON.parse(savedRelayCart);
					if (parsedRelayCart && parsedRelayCart.length > 0) {
						// Load cart into Redux state
						dispatch(loadPhase2Cart(parsedRelayCart));
						// Sync to backend (will only sync if user is logged in)
						dispatch(updatePhase2CartProduct());
					}
				} catch (e) {
					console.error("Error parsing Relay cart from localStorage:", e);
				}
			}

			// Also check for old localStorage key for backward compatibility
			const savedCartItems = localStorage.getItem('savedCartItems');
			if (savedCartItems && !savedSprintCart) {
				try {
					const parsedCartItems = JSON.parse(savedCartItems);
					if (parsedCartItems && parsedCartItems.length > 0) {
						// Migrate old cart to new format
						dispatch(loadCart(parsedCartItems));
						dispatch(updateCart(parsedCartItems));
						dispatch(updateCartProduct());
						// Save to new key
						localStorage.setItem('savedCartItems_sprint', savedCartItems);
						localStorage.removeItem('savedCartItems');
					}
				} catch (e) {
					console.error("Error parsing old cart from localStorage:", e);
				}
			}

			// Merge guest favorites with backend
			if (user && user.data && user.data.auth_token) {
				mergeGuestFavorites(user.data.auth_token);
				// If guest localStorage was empty, restore from users.*_cart_items
				dispatch(hydrateCartFromBackend());
			}

			return user;
		})
		.catch(function (error) {
			console.error("❌ [API DEBUG] loginWithOtp error occurred");
			console.error("❌ [API DEBUG] Error:", error);
			console.error("❌ [API DEBUG] Error response:", error.response);
			console.error("❌ [API DEBUG] Error message:", error.message);
			if (error.response) {
				console.error("❌ [API DEBUG] Error status:", error.response.status);
				console.error("❌ [API DEBUG] Error data:", error.response.data);
			}
			throw error;
		});
};

export const getWalletTransactions = (token, user_id) => (dispatch) => {
	return Axios.post(GET_WALLET_TRANSACTIONS_URL, {
		token: token,
		user_id: user_id,
	}, {
		headers: {
			"Content-Type": "application/json",
			...(token && { Authorization: `Bearer ${token}` }),
		},
	})
		.then((response) => {
			const wallet = response.data;
			return dispatch({ type: GET_WALLET_TRANSACTIONS, payload: wallet });
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const sendPasswordResetMail = (email) => (dispatch) => {
	Axios.post(SEND_PASSWORD_RESET_EMAIL_URL, {
		email: email,
	})
		.then((response) => {
			const data = response.data;
			return dispatch({ type: SEND_PASSWORD_RESET_EMAIL, payload: data });
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const verifyPasswordResetOtp = (email, code) => (dispatch) => {
	Axios.post(VERIFY_PASSWORD_RESET_OTP_URL, {
		email: email,
		code: code,
	})
		.then((response) => {
			const data = response.data;
			return dispatch({ type: VERIFY_PASSWORD_RESET_OTP, payload: data });
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const changeUserPassword = (email, code, password) => (dispatch) => {
	Axios.post(CHANGE_USER_PASSWORD_URL, {
		email: email,
		code: code,
		password: password,
	})
		.then((response) => {
			const data = response.data;
			return dispatch({ type: CHANGE_USER_PASSWORD, payload: data });
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const changeAvatar = (token, avatar) => () => {
	return Axios.post(CHANGE_USER_AVATAR_URL, {
		token: token,
		avatar: avatar,
	}, {
		headers: {
			"Content-Type": "application/json",
			...(token && { Authorization: `Bearer ${token}` }),
		},
	})
		.then((response) => {
			const data = response.data;
			return data;
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const checkBan = (token) => () => {
	return Axios.post(CHECK_BAN_URL, {
		token: token,
	}, {
		headers: {
			"Content-Type": "application/json",
			...(token && { Authorization: `Bearer ${token}` }),
		},
	})
		.then((response) => {
			const data = response.data;
			return data;
		})
		.catch(function (error) {
			console.log(error);
		});
};

export const deleteUserAccount = (token) => (dispatch) => {
	return Axios.post(
		DELETE_USER_ACCOUNT_URL,
		{},
		{
			headers: {
				"Content-Type": "application/json",
				...(token && { Authorization: `Bearer ${token}` }),
			},
		}
	)
		.then((response) => {
			if (response.data && response.data.success) {
				// Token already revoked by delete-account — skip /api/save-cart sync.
				return dispatch(logoutUser({ skipServerCartClear: true })).then(
					() => response.data
				);
			}
			return response.data;
		})
		.catch(function (error) {
			throw error;
		});
};

export const saveVATNumber = (token, vatNumber) => (dispatch) => {
	return Axios.post(SAVE_VAT_NUMBER_URL, {
		token: token,
		tax_number: vatNumber,
	}, {
		headers: {
			"Content-Type": "application/json",
			...(token && { Authorization: `Bearer ${token}` }),
		},
	})
		.then((response) => {
			const data = response.data;
			return dispatch({ type: SAVE_VAT_NUMBER, payload: data });
		})
		.catch(function (error) {
			console.log(error);
		});
};
